import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, RefreshCw, Clock, Zap, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../../services/api';
import { useConfirm } from '../../components/common/ConfirmDialog';

const COMPANIES = ['Keeneland', 'Fasig-Tipton', 'OBS', 'Other'];
const SALE_TYPES = ['2YO', 'Mixed', 'HORA', 'Yearling', 'Breeding Stock', 'Other'];

const BLANK_SEED = {
  company: '',
  sale_name: '',
  sale_type: '',
  book_day: '',
  seed_mph: '1.5',
  start_offset_mins: '5',
  notes: '',
};

const SeedForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState(initial || BLANK_SEED);
  const confirm = useConfirm();
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.company || !form.seed_mph) { toast.error('Company and Seed MPH are required'); return; }
    setSaving(true);
    try { await onSave(form); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Company *</Label>
          <Select value={form.company} onValueChange={v => setForm(f => ({ ...f, company: v }))}>
            <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select company" /></SelectTrigger>
            <SelectContent>
              {COMPANIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Sale Type</Label>
          <Select value={form.sale_type || '__all__'} onValueChange={v => setForm(f => ({ ...f, sale_type: v === '__all__' ? '' : v }))}>
            <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any</SelectItem>
              {SALE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Book / Day (optional)</Label>
          <Input value={form.book_day} onChange={e => setForm(f => ({ ...f, book_day: e.target.value }))}
            placeholder="e.g. Session 1, Session 2-3" className="h-8 text-sm" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Seed MPH (min/hip) *</Label>
          <Input type="number" step="0.1" min="0.1" value={form.seed_mph}
            onChange={e => setForm(f => ({ ...f, seed_mph: e.target.value }))}
            className="h-8 text-sm" />
          <p className="text-[10px] text-muted-foreground">Minutes per hip (e.g. 1.5 = 1.5 min/hip)</p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Start Offset (mins)</Label>
          <Input type="number" step="0.5" min="0" value={form.start_offset_mins}
            onChange={e => setForm(f => ({ ...f, start_offset_mins: e.target.value }))}
            className="h-8 text-sm" />
          <p className="text-[10px] text-muted-foreground">Delay added to advertised start time</p>
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Notes</Label>
        <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          placeholder="Any context about this seed" className="h-8 text-sm" />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving}
          className="bg-primary hover:bg-primary/90 text-white">
          {saving ? 'Saving…' : 'Save Seed'}
        </Button>
      </div>
    </div>
  );
};

const SaleWindowTab = () => {
  const [seeds, setSeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => { fetchSeeds(); }, []);

  const fetchSeeds = async () => {
    try {
      const res = await api.get('/sale-window/seeds');
      setSeeds(res.data || []);
    } catch { toast.error('Failed to load seeds'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (form) => {
    try {
      const res = await api.post('/sale-window/seeds', form);
      setSeeds(prev => [...prev, res.data]);
      setCreating(false);
      toast.success('Seed created');
    } catch { toast.error('Failed to create seed'); }
  };

  const handleUpdate = async (id, form) => {
    try {
      const res = await api.put(`/sale-window/seeds/${id}`, form);
      setSeeds(prev => prev.map(s => s.id === id ? res.data : s));
      setEditingId(null);
      toast.success('Seed updated');
    } catch { toast.error('Failed to update seed'); }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ title: 'Delete this seed?', destructive: true })) return;
    try {
      await api.delete(`/sale-window/seeds/${id}`);
      setSeeds(prev => prev.filter(s => s.id !== id));
      toast.success('Seed deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const grouped = seeds.reduce((acc, s) => {
    const key = s.company || 'Other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Sale Window Seeds</h2>
          <p className="text-sm text-muted-foreground">
            Seed MPH (minutes-per-hip) values used to estimate sale arrival times before live data is available.
            The system auto-learns and updates seeds after each completed sale session.
          </p>
        </div>
        {!creating && (
          <Button onClick={() => setCreating(true)} className="bg-primary hover:bg-primary/90 text-white">
            <Plus className="w-4 h-4 mr-2" /> New Seed
          </Button>
        )}
      </div>

      {/* Seed selection hierarchy guide */}
      <Card className="border-dashed">
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Seed Selection Priority</p>
          <div className="space-y-1 text-xs text-muted-foreground">
            <p><span className="font-medium text-foreground">1 (Most specific)</span> — Company + Sale Type + Book/Day</p>
            <p><span className="font-medium text-foreground">2 (Category)</span> — Company + Sale Type</p>
            <p><span className="font-medium text-foreground">3 (Company catch-all)</span> — Company + "Any" Sale Type</p>
            <p><span className="font-medium text-foreground">4 (Fallback)</span> — Any company matching Sale Type</p>
          </div>
        </CardContent>
      </Card>

      {creating && (
        <SeedForm
          onSave={handleCreate}
          onCancel={() => setCreating(false)}
        />
      )}

      {Object.keys(grouped).length === 0 && !creating ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          No seeds yet. Create seeds to enable sale window estimation.
        </CardContent></Card>
      ) : (
        Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([company, companySeeds]) => (
          <Card key={company}>
            <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(e => ({ ...e, [company]: !e[company] }))}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {expanded[company] !== false ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <CardTitle className="text-base">{company}</CardTitle>
                  <Badge variant="outline" className="text-[10px]">{companySeeds.length} seed{companySeeds.length !== 1 ? 's' : ''}</Badge>
                </div>
              </div>
            </CardHeader>
            {expanded[company] !== false && (
              <CardContent className="pt-0 space-y-3">
                {companySeeds.map(seed => (
                  <div key={seed.id}>
                    {editingId === seed.id ? (
                      <SeedForm
                        initial={{ ...seed, seed_mph: String(seed.seed_mph), start_offset_mins: String(seed.start_offset_mins) }}
                        onSave={(form) => handleUpdate(seed.id, form)}
                        onCancel={() => setEditingId(null)}
                      />
                    ) : (
                      <div className="flex items-start justify-between p-3 border rounded-lg hover:bg-muted/30">
                        <div className="space-y-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium">{seed.sale_type || 'Any Sale Type'}</span>
                            {seed.book_day && <Badge variant="outline" className="text-[10px]">{seed.book_day}</Badge>}
                            {seed.sample_count > 0 && (
                              <Badge className="text-[10px] bg-blue-100 text-blue-700">
                                <RefreshCw className="w-2.5 h-2.5 mr-1" />
                                {seed.sample_count} sample{seed.sample_count !== 1 ? 's' : ''}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Zap className="w-3 h-3" />
                              <strong className="text-foreground">{seed.seed_mph}</strong> min/hip
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              +{seed.start_offset_mins} min start offset
                            </span>
                            {seed.last_actual_mph && (
                              <span className="text-blue-600">Last actual: {seed.last_actual_mph} min/hip</span>
                            )}
                          </div>
                          {seed.notes && <p className="text-[11px] text-muted-foreground">{seed.notes}</p>}
                        </div>
                        <div className="flex gap-1 ml-3 shrink-0">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={() => setEditingId(seed.id)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            onClick={() => handleDelete(seed.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        ))
      )}
    </div>
  );
};

export default SaleWindowTab;
